import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AudienceApiService, AudienceVolumeTrendPoint } from '../../../services/audience-api.service';
import { AuthService } from '../../../services/auth.service';
import { AudienceExtractionsSparklineComponent } from './audience-extractions-sparkline.component';

/**
 * Global audience-extractions chart (advanced / admin_dashboard.all only). A collapsible card at the
 * top of the Reach › Audiences list showing how many audiences were extracted per day (last ~31d)
 * and are scheduled ahead (~31d). Entity-scoped server-side → reloads on sidebar entity switch.
 * Rendered only when the parent has already checked the advanced permission.
 */
@Component({
  selector: 'app-audience-extractions-chart',
  standalone: true,
  imports: [CommonModule, TranslateModule, AudienceExtractionsSparklineComponent],
  templateUrl: './audience-extractions-chart.component.html',
  styleUrls: ['./audience-extractions-chart.component.scss']
})
export class AudienceExtractionsChartComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  data: AudienceVolumeTrendPoint[] = [];
  loading = false;
  error = false;
  /**
   * Collapsed by default (advanced/monitoring, not the primary task). The choice is persisted, so a
   * user who opened it keeps it open ('0' = explicitly expanded); anything else → collapsed.
   */
  collapsed = sessionStorage.getItem('audiences.extractionsChart.collapsed') !== '0';

  private currentEntityId: string | null | undefined = undefined;

  constructor(
    private audienceApi: AudienceApiService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Always load (even collapsed): the header shows a mini sparkline preview of this same data.
    const sub = this.authService.selectedDcr$.pipe(distinctUntilChanged()).subscribe((entityId) => {
      if (entityId !== this.currentEntityId) {
        this.currentEntityId = entityId;
        this.load();
      }
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.audienceApi.getAudienceExecutions().subscribe({
      next: (rows) => {
        this.data = rows;
        this.loading = false;
      },
      error: () => {
        this.data = [];
        this.error = true;
        this.loading = false;
      }
    });
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    sessionStorage.setItem('audiences.extractionsChart.collapsed', this.collapsed ? '1' : '0');
  }

  get hasPreview(): boolean {
    return !this.loading && !this.error && this.data.length > 0;
  }
}
